import { Switch, Typography } from "antd";
import type { GraphFormState } from "../plotState";
import { MetricDropdownStep } from "../../shared/MetricDropdownStep";
import { countSourceFromMetricId } from "../../shared/CountIncludePicker";
import { GRAPH_COLORS } from "../../constants";

const { Text } = Typography;

interface SelectMetricsStepProps {
  graph: GraphFormState;
  graphIndex: number;
  onChange: (patch: Partial<GraphFormState>) => void;
}

export function SelectMetricsStep({ graph, graphIndex, onChange }: SelectMetricsStepProps) {
  const isGpsMetric = graph.selectedMetricId?.startsWith("gps-") ?? false;

  return (
    <>
      <MetricDropdownStep
        includeHelmet
        selectedMetricId={graph.selectedMetricId}
        metricAgg={graph.metricAgg}
        countSpec={graph.countSpec}
        onCountSpecChange={(countSpec) => onChange({ countSpec })}
        onSelectMetric={(value) => {
          const patch: Partial<GraphFormState> = { selectedMetricId: value };
          if (!value.startsWith("gps-") && graph.showAcwr) patch.showAcwr = false;
          // A count has no color strategy, so the backend sends every point with no
          // color of its own and the plot draws grey. The series color only applies
          // when the override is on (buildRaw.ts), so a count turns it on with the
          // color this plot already shows in the configurator and the preview.
          //
          // The override belongs to the count, so it leaves with it: a measured metric
          // brings its own color strategy, and keeping a flat override would hide it.
          // This mirrors the aggregation, which is forced to "sum" for a count and
          // restored to "avg" on the way out.
          //
          // Read from the id the step is replacing, not from countSpec: this handler
          // runs before the spec is cleared, so the id is the one reliable answer to
          // "was this a count a moment ago".
          const wasCount = countSourceFromMetricId(graph.selectedMetricId) !== null;
          const isCount = countSourceFromMetricId(value) !== null;
          if (isCount && !graph.enableColorOverride) {
            patch.enableColorOverride = true;
            patch.colorOverride =
              graph.colorOverride || GRAPH_COLORS[graphIndex % GRAPH_COLORS.length];
          } else if (!isCount && wasCount) {
            patch.enableColorOverride = false;
            patch.colorOverride = "";
          }
          onChange(patch);
        }}
        onAggChange={(agg) => onChange({ metricAgg: agg })}
      />
      {graph.selectedMetricId != null && isGpsMetric && (
        <div className="awm-step-body" style={{ marginTop: 8 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Text style={{ fontSize: 12 }}>
              ACWR
              <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>
                (Acute:Chronic Workload Ratio)
              </Text>
            </Text>
            <Switch
              size="small"
              checked={graph.showAcwr}
              onChange={(checked) =>
                onChange({ showAcwr: checked, acwrRefLines: checked ? graph.acwrRefLines : false })
              }
            />
          </div>
          {graph.showAcwr && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Text style={{ fontSize: 12 }}>ACWR Standard Reference Lines</Text>
              <Switch
                size="small"
                checked={graph.acwrRefLines}
                onChange={(checked) => onChange({ acwrRefLines: checked })}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
}
